import { API_ENDPOINTS } from '@/api/endpoints';
import { apiFetch } from '@/util/apiClient';
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  MenuItem,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrg } from '../../organization/useOrg';
import { Project } from '@/stores/ProjectStore';
import Grid from '@mui/material/Grid2';
import { useState, useEffect } from 'react';
import { InfoBox } from './InfoBox';
import { useGetProject } from '@/queries/useProjects';
import { useFiscalYears } from '@/fiscalYears/useFiscalYears';
import { BaseDecarbFooterBar } from '@/decarbonization/components/BaseDecarbFooterBar';
import { usePencilsDown } from '@/decarbonization/hooks/usePencilsDown';
import { fiscalYearEndpoints } from '@/api/endpoints/fiscalYears';
import { FiscalYear as FiscalYearType } from '@/types/fiscalYear';
import { MajorMaintWarning } from './MajorMaintWarning';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';

export function ProjectOverview() {
  const { selectedOrg } = useOrg();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const pencilsDown = usePencilsDown();
  const queryClient = useQueryClient();
  const create_proj: boolean = projectId === undefined;
  const { data: project } = useGetProject(selectedOrg?.id, projectId);
  const { data: fiscalYears } = useFiscalYears(selectedOrg?.id || '');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [projTitle, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [userProjId, setUserProjId] = useState('');
  const [status, setStatus] = useState('New Construction');
  const [newFisc, setNewFisc] = useState('');
  const [firstFund, setFirstFund] = useState('');
  const [firstOp, setFirstOp] = useState('');
  const [firstConstruct, setFirstConstruct] = useState('');
  const [projectMaint, setProjectMain] = useState('');
  const [projDesign, setProjDesign] = useState('');
  const [projMaintWasEdited, setProjMaintEdit] = useState(false);
  const [override, setOverride] = useState(false);
  const permsQuery = useOrganizationPerms(selectedOrg?.id);
  const [save, setSave] = useState(false);
  const [error, setError] = useState<{
    lifetime?: string;
    maintenance?: string;
    name?: string;
    fiscalYear?: string;
    fiscalYearYear?: string;
    general?: string;
    userProjId?: string;
    operation?: string;
    construction?: string;
    funding?: string;
    status?: string;
    address?: string;
    description?: string;
  }>({});
  const nextButtonIsDisabled =
    !newFisc ||
    !projTitle ||
    !status ||
    !userProjId ||
    !address ||
    !description ||
    !projDesign;

  useEffect(() => {
    if (fiscalYears) {
      const curFiscalYear = fiscalYears.find(
        (fy) => fy.id === project?.fiscalYear,
      );
      const fundYear = fiscalYears.find(
        (fy) => fy.id === project?.firstFundingYear,
      );
      const opYear = fiscalYears.find(
        (fy) => fy.id === project?.firstOperationYear,
      );
      const constYear = fiscalYears.find(
        (fy) => fy.id === project?.firstConstructionYear,
      );
      setNewFisc(curFiscalYear?.name.replace('FY ', '') || '');
      setFirstFund(fundYear?.name.replace('FY ', '') || '');
      setFirstOp(opYear?.name.replace('FY ', '') || '');
      setFirstConstruct(constYear?.name.replace('FY ', '') || '');
    }
  }, [
    fiscalYears,
    project?.fiscalYear,
    project?.firstFundingYear,
    project?.firstOperationYear,
    project?.firstConstructionYear,
  ]);

  useEffect(() => {
    if (project) {
      const curFiscalYear = fiscalYears?.find(
        (fy) => fy.id === project.fiscalYear,
      );
      const fundYear = fiscalYears?.find(
        (fy) => fy.id === project.firstFundingYear,
      );
      const opYear = fiscalYears?.find(
        (fy) => fy.id === project.firstOperationYear,
      );
      const constYear = fiscalYears?.find(
        (fy) => fy.id === project.firstConstructionYear,
      );
      setNewFisc(curFiscalYear?.name.replace('FY ', '') || '');
      setFirstFund(fundYear?.name.replace('FY ', '') || '');
      setFirstOp(opYear?.name.replace('FY ', '') || '');
      setFirstConstruct(constYear?.name.replace('FY ', '') || '');
      setName(project.name || '');
      setDescription(project.description || '');
      setAddress(project.address || '');
      setUserProjId(project.userProjectId || '');
      setProjectMain(project.projectMajorMaintenanceYears?.toString() || '');
      setProjDesign(project.projectLifetimeYears?.toString() || '');
      setStatus(project.status || 'New Construction');
    }
  }, [project, fiscalYears]);

  // either update project or edit project
  const handleBackClick = () => {
    // hit the back button, so do not save changes
    if (create_proj) {
      navigate('/landing');
      return;
    }
    navigate('../proj-details');
    return;
  };

  const saveProj = async ({ yes = false, no = false } = {}): Promise<
    Project | undefined
  > => {
    setOverride(false);
    setError({});
    const errors: typeof error = {};
    // check if major maintenance changed
    if (
      !create_proj &&
      project !== undefined &&
      !yes &&
      project.projectMajorMaintenanceYears !== undefined &&
      projectMaint !== project.projectMajorMaintenanceYears.toString()
    ) {
      if (no) {
        setOverride(true);
      }
      setProjMaintEdit(true);
      return;
    }
    try {
      if (selectedOrg === null) return undefined;
      const fiscalYearId = await handleFiscalYear(newFisc);
      // first funding/operation/construction years are optional
      const fundingYear = firstFund ? await handleFiscalYear(firstFund) : null;
      const operatingYear = firstOp ? await handleFiscalYear(firstOp) : null;
      const constructYear = firstConstruct
        ? await handleFiscalYear(firstConstruct)
        : null;
      // Prepare project data
      const projectData = {
        ...project,
        name: projTitle,
        description: description,
        address: address,
        projectLifetimeYears: parseInt(projDesign) || 100,
        projectMajorMaintenanceYears: parseInt(projectMaint) || 0,
        organization: selectedOrg.id,
        fiscalYear: fiscalYearId,
        userProjectId: userProjId,
        firstFundingYear: fundingYear,
        firstOperationYear: operatingYear,
        firstConstructionYear: constructYear,
        status: status,
      };
      let id = undefined;
      if (create_proj) {
        id = await apiFetch<Project>(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.CREATE(selectedOrg.id),
          {
            method: 'POST',
            body: JSON.stringify(projectData),
          },
        );
      } else {
        // just edit
        if (project === undefined) return;
        id = await apiFetch<Project>(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(
            selectedOrg.id,
            project.id,
          ),
          {
            method: 'PUT',
            body: JSON.stringify({ ...projectData, id: project?.id }),
          },
        );
      }
      const targetProjectId = create_proj ? id?.id : project?.id;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['organization', selectedOrg.id, 'fiscal-years'],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: ['projects', selectedOrg?.id],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: ['projects-with-subprojects', selectedOrg?.id],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: ['project', selectedOrg?.id, targetProjectId],
          refetchType: 'active',
        }),
        queryClient.invalidateQueries({
          queryKey: [selectedOrg?.id, targetProjectId],
          refetchType: 'active',
        }),
      ]);

      setName('');
      setAddress('');
      setDescription('');
      return id;
    } catch (error) {
      console.error('Error creating project:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create project. Please try again.';
      errors.general = errorMessage;
      setError(errors);
    }
  };

  const handleNextClick = async ({ yes = false, no = false } = {}) => {
    setSave(false);
    const id: Project | undefined = await saveProj({ yes: yes, no: no });
    if (id === undefined) return;
    navigate(`/project/${id.id}/managerial`);
  };

  const handleSaveClick = async ({ yes = false, no = false } = {}) => {
    setSave(true);
    const id: Project | undefined = await saveProj({ yes: yes, no: no });
    if (id === undefined) return;
    navigate(`/project/${id.id}/proj-details`);
  };

  const handleFiscalYear = async (newYear: string) => {
    const errors: typeof error = {};
    const year = parseInt(newYear, 10);
    if (selectedOrg === null) return null;
    if (isNaN(year) || year < 2000 || year > 2100) {
      errors.fiscalYearYear = 'Please enter a valid year (2000-2100)';
      setError(errors);
    }
    const fyName = `FY ${year}`;
    const currentCache = queryClient.getQueryData<FiscalYearType[]>([
      'organization',
      selectedOrg.id,
      'fiscal-years',
    ]);
    const existingFY = currentCache?.find((fy) => fy.name === fyName);
    let fiscalYearId = '';
    try {
      if (existingFY) {
        // Use existing fiscal year
        fiscalYearId = existingFY.id;
        return fiscalYearId;
      } else {
        // Create new fiscal year
        const newFiscalYear = await apiFetch<FiscalYearType>(
          fiscalYearEndpoints.CREATE(selectedOrg.id),
          {
            method: 'POST',
            body: JSON.stringify({
              name: fyName,
              startDate: `${year}-01-01`,
              endDate: `${year}-12-31`,
              isActive: false,
            }),
          },
        );
        fiscalYearId = newFiscalYear.id;
        queryClient.setQueryData<FiscalYearType[]>(
          ['organization', selectedOrg.id, 'fiscal-years'],
          (fiscalYears) => {
            const previous = Array.isArray(fiscalYears) ? fiscalYears : [];
            return [...previous, newFiscalYear];
          },
        );
        return fiscalYearId;
      }
    } catch (e) {
      console.error('Error creating new fiscal year:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create fiscal year. Please try again.';
      errors.general = errorMessage;
      setError(errors);
    }
  };

  if (permsQuery.isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>Loading....</Typography>
      </Box>
    );
  }

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  if (!permsQuery.data?.canManageProjs) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          You do not have permission to create projects.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Title of page */}
        <Box
          sx={{
            mt: '35px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            pointerEvents: 'auto',
            ml: '70px',
            flexShrink: 0,
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component='h1'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(24px, 1.85vw, 35px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
              mb: 'clamp(4px, 0.4vw, 8px)',
            }}
          >
            Project Creation
          </Typography>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component='h2'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(45px, 3.5vw, 68px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
            }}
          >
            {project?.name || 'New Project'}
          </Typography>
        </Box>
        <Container
          maxWidth={false}
          sx={{
            width: '97%',
            maxWidth: '2400px',
            mt: 5,
            mb: 3,
            px: 10,
            display: 'flex',
            flexDirection: 'column !important',
            minHeight: 0,
            maxHeight: '85vh',
            textTransform: 'uppercase',
          }}
        >
          <Paper
            sx={{
              bgcolor: 'primary.dark',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant={'h6'}>General Overview</Typography>
              <Typography variant={'subtitle1'} sx={{ mt: '3px' }}>
                Tell us about your project and the timeframe
              </Typography>
            </Box>
          </Paper>
          <Grid
            container
            spacing={10}
            sx={{
              bgcolor: 'primary.light',
              p: 6,
              display: 'flex',
              justifyContent: 'center',
              textTransform: 'uppercase',
              overflow: 'auto',
              flexGrow: 1,
              overflowY: 'auto',
              minHeight: 0,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            <Grid size={7} maxWidth={'lg'} sx={{ mt: -2 }}>
              <Paper
                sx={{
                  bgcolor: 'primary.dark',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  p: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  textTransform: 'uppercase',
                }}
              >
                <Typography variant={'subtitle2'}>Project Details</Typography>
              </Paper>
              <Container
                sx={{
                  bgcolor: 'primary.main',
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  p: 7,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  textTransform: 'uppercase',
                }}
              >
                <Stack width='100%' sx={{ gap: 3 }}>
                  <Box sx={{ mt: 5, display: 'flex', gap: 4 }}>
                    <InfoBox
                      title='Project Title'
                      lastVal={projTitle}
                      onChange={setName}
                      error={error.name}
                    ></InfoBox>
                    <InfoBox
                      title='Project ID'
                      lastVal={userProjId}
                      onChange={setUserProjId}
                      error={error.userProjId}
                    ></InfoBox>
                    <Paper
                      sx={{
                        bgcolor: 'info.main',
                        borderRadius: 1,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        textTransform: 'uppercase',
                        flex: '1 0 10%',
                      }}
                    >
                      <Typography
                        variant={'subtitle2'}
                        sx={{ fontSize: 10, mb: 3, color: 'primary.main' }}
                      >
                        Status *
                      </Typography>
                      <TextField
                        select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        fullWidth
                        required
                        error={!!error.status}
                        helperText={error.status}
                        autoFocus
                        variant='standard'
                        sx={{
                          '& .MuiInputBase-input': { color: 'primary.main' },
                        }}
                      >
                        <MenuItem value='New Construction'>
                          New Construction
                        </MenuItem>
                        <MenuItem value='Renewal'>Renewal</MenuItem>
                      </TextField>
                    </Paper>
                  </Box>
                  <InfoBox
                    title='Address'
                    lastVal={address}
                    onChange={setAddress}
                    error={error.address}
                  ></InfoBox>
                  <InfoBox
                    title='Description'
                    lastVal={description}
                    onChange={setDescription}
                    error={error.description}
                  ></InfoBox>
                </Stack>
              </Container>
            </Grid>
            <Grid size={5} maxWidth={'lg'} sx={{ mt: -2 }}>
              <Paper
                sx={{
                  bgcolor: 'primary.dark',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  p: 3,
                  display: 'flex',
                  alignItems: 'flex-end',
                  textTransform: 'uppercase',
                }}
              >
                <Typography variant={'subtitle2'}>Timeframes</Typography>
              </Paper>
              <Container
                sx={{
                  bgcolor: 'primary.main',
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  p: 7,
                  textTransform: 'uppercase',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box>
                  <Stack width='100%' sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ mt: 5, display: 'flex', gap: 2 }}>
                      <InfoBox
                        title='Fiscal Year'
                        lastVal={newFisc}
                        onChange={setNewFisc}
                        error={error.fiscalYearYear}
                        fy={true}
                      ></InfoBox>
                      <InfoBox
                        title='First Year of Funding'
                        lastVal={firstFund}
                        onChange={setFirstFund}
                        error={error.funding}
                        fy={true}
                        required={false}
                      ></InfoBox>
                    </Box>
                    <Box
                      sx={{
                        mt: 5,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                      }}
                    >
                      <InfoBox
                        title='First Year of Operation'
                        lastVal={firstOp}
                        onChange={setFirstOp}
                        error={error.operation}
                        fy={true}
                        required={false}
                      ></InfoBox>
                      <InfoBox
                        title='First Year of Construction'
                        lastVal={firstConstruct}
                        onChange={setFirstConstruct}
                        error={error.construction}
                        fy={true}
                        required={false}
                      ></InfoBox>
                    </Box>
                    <Box
                      sx={{
                        mt: 5,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                      }}
                    >
                      <InfoBox
                        title='Projected Maintenance Cycle'
                        lastVal={projectMaint}
                        onChange={setProjectMain}
                        error={error.maintenance}
                        na={true}
                        required={false}
                      ></InfoBox>
                      <InfoBox
                        title='Projected Design Life'
                        lastVal={projDesign}
                        onChange={setProjDesign}
                        error={error.lifetime}
                      ></InfoBox>
                    </Box>
                  </Stack>
                </Box>
              </Container>
            </Grid>
          </Grid>
          {projMaintWasEdited && !override && (
            <MajorMaintWarning
              func={save ? handleSaveClick : handleNextClick}
            ></MajorMaintWarning>
          )}
        </Container>
      </Box>
      <Box sx={{ flexShrink: 0, width: '100%' }}>
        <BaseDecarbFooterBar
          backText={'Cancel'}
          onBackClick={handleBackClick}
          onNextClick={handleNextClick}
          nextIsDisabled={nextButtonIsDisabled}
          nextIsDisabledText={'INCOMPLETE DATA'}
          onReturnToHubClick={handleSaveClick}
          returnText={
            pencilsDown ? 'RETURN TO SUMMARY' : 'SAVE & EXIT TO SUMMARY'
          }
        />
      </Box>
    </>
  );
}
